import React, { useEffect, useRef, useState } from "react";
import ConfirmDeleteModal from "@shared/components/ConfirmationModal";
import PageContentFilterDropdown from "@shared/components/PageContentFilterDropdown";
import CloudSyncDropdown from "./Tabs/components/Files/CloudSyncDropdown";
import NewItemButton from "@shared/components/NewItemButton";
import ShareMenu from "@shared/components/ShareProject/ShareProjectModal";
import AvatarList from "@pages/Home/components/AvatarList";
import { toastService } from "@services/ToastService";
import { FiPlus } from "react-icons/fi";
import { Member } from "@shared/interfaces/User";
import { LocalFolder } from "@services/LocalFolderService";
import { ProjectRole } from "@utils/permissions";

// ===== Types =====
export type DropdownFilter = "Latest" | "Oldest";

export interface OwnerInfo {
  ownerFirstName: string;
  ownerLastName: string;
}

export interface FolderView {
  parent_id: number | null;
  folders: LocalFolder[];
  files: any[];
  breadcrumbs: {
    id: number;
    name: string;
  }[];
}

export interface ProjectPageHeaderProps {
  // Header context
  activeTab: string;

  // Report creation
  handleGenerateReport: () => void;

  // Selection & bulk actions
  checkedItems: string[];
  handleDeleteSelectedItems: () => Promise<void>;
  handleDownloadSelectedItems: () => void;

  // Edit state toggles
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  toggleDeleteModal: () => void;

  // Viewer/permissions
  isViewer: boolean; // computed boolean from isOwner/permissions
  isOwner: boolean;
  permissions: string[];
  hasProjectRole: (userPermissions: string[], role: ProjectRole) => boolean;

  // Overview edit actions
  handleEditClick: () => void;
  handleDiscardChanges: () => void;
  isFormValid: boolean;

  // Shares / members (external state—passed in to avoid logic changes)
  users: Array<any>;
  owner: OwnerInfo;
  projectId?: string;
  isMemberModalOpen: boolean;
  isShareMenuOpen: boolean;
  handleMemberButtonClick: (e: React.MouseEvent) => void;
  handleShareButtonClick: () => void;
  handleMemberUpdate: (updatedMembers: Member[]) => void;

  // Refs used for outside-click handling in your app
  memberButtonRef: React.RefObject<HTMLButtonElement | null>;
  reducedShareMenuRef: React.RefObject<HTMLDivElement | null>;
  shareButtonRef: React.RefObject<HTMLButtonElement | null>;
  shareMenuRef: React.RefObject<HTMLDivElement | null>;

  // Files/Documents context
  canViewProjectFiles: boolean;
  refreshCloudFiles: () => void;

  // Folder root "New" (moved from parent)
  folderLoading: boolean;
  fileCount: number;
  folderView: FolderView | null;
  startCreateFolder: () => void;

  // Reports tab filter
  dropdownFilter: DropdownFilter;
  setDropdownFilter: (v: DropdownFilter) => void;

  // Loading flags
  loading?: boolean;
  fetchingFolderFiles?: boolean;
  tabsDisabled?: boolean;

  refreshGDriveFiles: () => void;
  refreshOneDriveFiles: () => void;
}

// Extend Window (as in your original file)
declare global {
  interface Window {
    google: any;
    gapi: {
      load: (library: string, callback?: () => void) => void;
      picker?: any;
    };
    OneDrive?: any;
  }
}

const ProjectPageHeader: React.FC<ProjectPageHeaderProps> = ({
  // context
  activeTab,

  // report creation
  handleGenerateReport,

  // selection actions
  checkedItems,
  handleDeleteSelectedItems,
  handleDownloadSelectedItems,

  // edit state
  isEditing,
  setIsEditing,
  toggleDeleteModal,

  // viewer/permissions
  isViewer,
  isOwner,
  permissions,
  hasProjectRole,

  // overview actions
  handleEditClick,
  handleDiscardChanges,
  isFormValid,

  // shares/members
  users,
  owner,
  projectId,
  isMemberModalOpen,
  isShareMenuOpen,
  handleMemberButtonClick,
  handleShareButtonClick,
  handleMemberUpdate,
  memberButtonRef,
  reducedShareMenuRef,
  shareButtonRef,
  shareMenuRef,

  // files/docs
  canViewProjectFiles,
  refreshCloudFiles,

  // folder root "New"
  folderLoading,
  fileCount,
  folderView,
  startCreateFolder,

  // reports filter
  dropdownFilter,
  setDropdownFilter,

  // misc flags
  loading,
  fetchingFolderFiles,
  tabsDisabled,

  refreshGDriveFiles,
  refreshOneDriveFiles,
}) => {
  // ========== Local state moved with the JSX ==========
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [isNewItemMenuOpen, setIsNewItemMenuOpen] = useState(false);

  // LaunchDarkly flag & Google Picker script (unchanged)
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://apis.google.com/js/api.js";
    script.async = true;
    script.onload = () => {
      if (window.gapi) window.gapi.load("picker");
    };
    document.body.appendChild(script);
  }, []);

  // Bulk delete modal
  const handleOpenDeleteModal = () => {
    if (checkedItems.length > 0) setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await handleDeleteSelectedItems();
      toastService.success(
        `${checkedItems.length === 1 ? "Item" : "Items"} deleted successfully`
      );
      setIsModalOpen(false);
    } catch (error) {
      toastService.error("Failed to delete items");
      // eslint-disable-next-line no-console
      console.error("Error deleting items:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* === Existing header controls (kept) === */}
      <div className="flex items-center justify-between w-full ">
        <div className="w-full flex h-8 mb-2">
          {activeTab === "Overview" && (
            <>
              {/* Project Delete Button */}
              {isEditing && (
                <div className="flex-1 justify-center flex text-sm 2xl:text-base">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDeleteModal();
                    }}
                    className="h-10 py-2 px-4 flex items-center gap-2 rounded-lg bg-white text-gray-700 border-gray-300 border-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      className="2xl:w-5 2xl:h-5 w-4 h-4"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9 3a3 3 0 0 1 3-3h1a3 3 0 0 1 3 3h4a1 1 0 1 1 0 2H2a1 1 0 1 1 0-2h4a3 3 0 0 1 3-3h1Zm2 0h2a1 1 0 0 1 1 1h-4a1 1 0 0 1 1-1ZM4 6h16v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Zm5 4a1 1 0 1 1 2 0v6a1 1 0 1 1-2 0v-6Zm5-1a1 1 0 1 1 2 0v7a1 1 0 1 1-2 0v-7Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === "Documents" && (
            <>
              {/* === Moved: Root-level "New" button shown at empty root === */}
              {!folderLoading &&
                fileCount === 0 &&
                (folderView?.breadcrumbs.length ?? 0) === 0 &&
                activeTab === "Documents" && (
                  <div className="relative mr-2" ref={menuRef}>
                    <button
                      type="button"
                      className="flex items-center text-xs gap-2 px-3 h-10 rounded-md border border-[#CFD4DC] bg-white font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => setIsNewItemMenuOpen((v) => !v)}
                      aria-haspopup="menu"
                      aria-expanded={isNewItemMenuOpen}
                    >
                      <span className="text-lg leading-none">
                        <FiPlus />
                      </span>
                      New
                    </button>

                    {isNewItemMenuOpen && (
                      <div
                        role="menu"
                        className="absolute right-0 mt-2 w-44 rounded-md border border-gray-200 bg-white shadow-lg z-20"
                      >
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2.5 rounded-b-md text-sm hover:bg-gray-100 flex space-x-2 items-center"
                          onClick={startCreateFolder}
                        >
                          <img src="/new-folder.svg" className="w-5 h-5" />
                          <span className="text-sm 2xl:text-base whitespace-nowrap">
                            New Folder
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
            </>
          )}

          {activeTab === "Reports" && (
            <div className="flex-1 justify-center flex ">
              <PageContentFilterDropdown
                setDropdownFilter={(filter) =>
                  setDropdownFilter(filter as DropdownFilter)
                }
                dropdownFilter={dropdownFilter}
              />
            </div>
          )}
        </div>

        {/* Documents Tab Actions */}
        {activeTab === "Documents" && (
          <div className="flex space-x-2 h-10">
            {checkedItems.length > 0 && (
              <div className="flex items-center border-2 border-gray-100 rounded-lg">
                <div className="flex items-center px-3 text-blue-600 font-medium text-sm 2xl:text-base">
                  <span>{checkedItems.length}</span>
                  <span className="ml-1">selected</span>
                </div>

                {/* Download hidden when any folder is selected (unchanged) */}
                {checkedItems.filter((id) => id.endsWith("-folder")).length ===
                  0 && (
                  <button
                    type="button"
                    className="flex text-sm 2xl:text-base items-center space-x-2 text-gray-700 font-semibold py-1.5 px-3 border-l-2 border-gray-100"
                    onClick={handleDownloadSelectedItems}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 4v12m0 0l-3-3m3 3l3-3m-3 3V4m8 8a8 8 0 10-16 0 8 8 0 0016 0z"
                      />
                    </svg>
                    <span className="hover:text-blue-600 transition duration-200">
                      Download
                    </span>
                  </button>
                )}

                {!isViewer && (
                  <button
                    className="flex text-sm 2xl:text-base items-center space-x-2 font-semibold px-3 py-1.5 rounded-e border-l-2 border-gray-100"
                    onClick={handleOpenDeleteModal}
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="2xl:h-5 2xl:w-5 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span className="hover:text-red-500 transition duration-200">
                      Delete
                    </span>
                  </button>
                )}
              </div>
            )}

            {canViewProjectFiles && (
              <div className="flex-1 justify-end flex items-center space-x-2">
                {/* Cloud sync dropdown gated by LD flag */}

                <CloudSyncDropdown
                  triggerRefreshGDriveFiles={refreshGDriveFiles}
                  triggerRefreshOneDriveFiles={refreshOneDriveFiles}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Moved: row with New Report / Members / Edit/Share controls === */}
      <div className="flex relative">
        {/* OverviewTab Actions */}
        {activeTab === "Overview" && (
          <>
            {!isEditing && users.length > 0 && (
              <div className="flex items-center relative ml-1 mr-3">
                <span ref={memberButtonRef}>
                  <AvatarList
                    shared_with={users}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleMemberButtonClick(e);
                    }}
                    size={10}
                  />
                </span>

                {isMemberModalOpen && projectId && (
                  <div ref={reducedShareMenuRef}>
                    <ShareMenu
                      isOpen={isMemberModalOpen}
                      projectId={projectId}
                      ownerFirstName={owner.ownerFirstName}
                      ownerLastName={owner.ownerLastName}
                      reducedVersion={true}
                      isAdmin={
                        isOwner || permissions.includes("CAN_MANAGE_ACCESS")
                      }
                      onMemberUpdate={handleMemberUpdate}
                    />
                  </div>
                )}
              </div>
            )}

            {(isOwner || hasProjectRole(permissions, "FULL_ACCESS")) &&
              (isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={handleDiscardChanges}
                    className="py-2 h-10 whitespace-nowrap px-4 text-sm 2xl:text-base flex justify-center items-center border-2 border-blue-700 rounded-md font-medium text-blue-700 hover:text-blue-600 transition relative mr-4 "
                  >
                    <img
                      src="/close_small.svg"
                      className="w-3 h-3 mr-3"
                      alt=""
                    />
                    <span>Discard</span>
                  </button>
                  <button
                    type="submit"
                    className="py-2 h-10 whitespace-nowrap px-4 text-sm 2xl:text-base rounded-md text-white bg-blue-700 hover:bg-blue-600 transition relative  disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={!isFormValid}
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="py-2 px-4 h-10 text-sm 2xl:text-base rounded-md font-medium text-blue-700 hover:text-blue-600 transition relative mr-4 "
                >
                  Edit
                </button>
              ))}

            {(isOwner || permissions.includes("CAN_MANAGE_ACCESS")) &&
              !isEditing && (
                <>
                  <button
                    ref={shareButtonRef}
                    type="button"
                    onClick={handleShareButtonClick}
                    className="py-2 h-10 px-4 rounded-md text-white bg-blue-700 hover:bg-blue-600 transition relative "
                  >
                    Share
                  </button>
                  {isShareMenuOpen && projectId && (
                    <div ref={shareMenuRef}>
                      <ShareMenu
                        isOpen={isShareMenuOpen}
                        projectId={projectId}
                        ownerFirstName={owner.ownerFirstName}
                        ownerLastName={owner.ownerLastName}
                        reducedVersion={false}
                        isAdmin={true}
                        onMemberUpdate={handleMemberUpdate}
                      />
                    </div>
                  )}
                </>
              )}
          </>
        )}

        {/* Report Gen Btn */}
        {(activeTab === "Documents" || activeTab === "Reports") &&
          (permissions.includes("CAN_CREATE_REPORTS") || isOwner) && (
            <NewItemButton
              onClick={handleGenerateReport}
              title="Report"
              disabled={!!fetchingFolderFiles}
              isLoading={!!fetchingFolderFiles}
            />
          )}
      </div>

      {/* === Bulk Delete Confirm Modal === */}
      <ConfirmDeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmDelete}
        entityNames={checkedItems}
        isLoading={deleting}
      />
    </>
  );
};

export default ProjectPageHeader;
